import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@dayvise/react-ui";
import { useActiveWorkspaceId, useDayviseClient, useSession } from "./providers.tsx";
import { queryKeys } from "./queryKeys.ts";

/** Upload privately, then let the user review and send the import command. */
export function ProfileArchiveUpload({ conversationId, onCommand, disabled = false }: { conversationId: string; onCommand: (text: string) => void; disabled?: boolean }) {
  const client = useDayviseClient();
  const workspaceId = useActiveWorkspaceId();
  const { session } = useSession();
  const cache = useQueryClient();
  const identity = JSON.stringify([workspaceId, session.account?.userId ?? null, conversationId, disabled]);
  const current = useRef<string | null>(identity);
  const abort = useRef<AbortController | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  if (current.current !== identity) abort.current?.abort();
  current.current = identity;
  const [state, setState] = useState({ identity, busy: false, error: "", command: "" });
  useEffect(() => {
    current.current = identity;
    setState({ identity, busy: false, error: "", command: "" });
    return () => { current.current = null; abort.current?.abort(); };
  }, [identity]);
  const upload = async (file: File) => {
    if (disabled || abort.current && !abort.current.signal.aborted) return;
    const controller = new AbortController(); abort.current = controller;
    const active = () => current.current === identity && !controller.signal.aborted;
    setState({ identity, busy: true, error: "", command: "" });
    try {
      if (!file.size || file.size > 16 * 1024 * 1024 || !/\.(?:tar\.gz|tgz)$/i.test(file.name)) throw new Error("Choose a .tar.gz or .tgz Dayvise profile archive, up to 16 MiB.");
      const bytes = new Uint8Array(await file.arrayBuffer());
      if (!active()) return;
      const basename = file.name.replace(/[\\/:<>"|?*\u0000-\u001f\u007f]/g, "_").slice(-160);
      const result = await client.files.upload({ path: `profile-uploads/${conversationId}/${crypto.randomUUID()}/${basename}`, bytes, contentType: "application/gzip", conversationId }, { signal: controller.signal });
      if (!active()) return;
      setState({ identity, busy: false, error: "", command: `/import ${result.file.fileId}` });
      void cache.invalidateQueries({ queryKey: queryKeys.files({ workspaceId }) });
    } catch (error) { if (active()) setState({ identity, busy: false, error: error instanceof Error ? error.message : "Profile upload failed.", command: "" }); }
    finally { if (abort.current === controller) abort.current = null; }
  };
  const visible = state.identity === identity ? state : null;
  return <div>
    <input ref={picker} type="file" accept=".tar.gz,.tgz,application/gzip" aria-label="Profile archive" hidden onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ""; if (file) void upload(file); }} />
    <Button size="sm" variant="secondary" disabled={disabled || visible?.busy} onClick={() => picker.current?.click()}>{visible?.busy ? "Uploading profile…" : "Import profile"}</Button>
    {visible?.error ? <p role="alert">{visible.error}</p> : null}
    {visible?.command && !disabled ? <div><p>Archive uploaded to this private conversation.</p><Button size="sm" onClick={() => onCommand(visible.command)}>Prepare import command</Button><code>{visible.command}</code></div> : null}
  </div>;
}
